import { Box, Button, Modal, Typography, Chip, Collapse } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Fragment, useState, useRef, useLayoutEffect, useContext } from "react";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { getTeamUrlV2, viewLogsUrl } from "src/react-query/endPoints";
import { fetchInfinite } from "src/react-query/fetchWithError";
import { yearFirstDateTimeFormat2 } from "src/utils/formatTime";
import { yearFirstDateTimeFormatInTimezone } from "src/utils/timezoneFormat";
import { Context } from "src/context";
import InfiniteScroll from "react-infinite-scroll-component";
import { Icon } from "@iconify/react";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "900px",
  bgcolor: "background.paper",
  borderRadius: 1,
  boxShadow: 24,
  p: 4,
  maxHeight: "90vh",
};

export const ViewLogsModal = ({ open, onClose, chargerId }) => {
  const { teamId } = useSelectedTeam();
  const { data, hasNextPage, fetchNextPage } = useInfiniteQuery(
    ["viewLogs", { url: getTeamUrlV2(viewLogsUrl(chargerId)) }],
    fetchInfinite,
    {
      getNextPageParam: (lastPage, pages) => {
        if (lastPage.length === 0) {
          return;
        }
        return pages.length + 1;
      },
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      keepPreviousData: true,
    }
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <LogsData data={data} fetchNextPage={fetchNextPage} hasNextPage={hasNextPage}/>
        <Button
          variant="outlined"
          onClick={onClose}
          size="large"
          sx={{ marginTop: 3 }}
        >
          Close
        </Button>
      </Box>
    </Modal>
  );
};


export const LogsData = ({data,fetchNextPage,hasNextPage})=>{
  const theme = useTheme();
  const { timezone } = useContext(Context);
  const formatLogTime = (messageTime) =>
    timezone.timezone?.value
      ? yearFirstDateTimeFormatInTimezone(messageTime, timezone.timezone.value)
      : yearFirstDateTimeFormat2(messageTime);
  const [expandedRow, setExpandedRow] = useState(null);
  const scrollRef = useRef(null);
  const savedScrollTop = useRef(0);
  const logBodyBg = theme.palette.mode === "dark"
    ? alpha(theme.palette.common.white, 0.06)
    : theme.palette.grey[100];
  const headerBg = theme.palette.mode === "dark"
    ? alpha(theme.palette.common.white, 0.04)
    : theme.palette.grey[100];

  // OUTGOING = sent by CMS, INCOMING = sent by the Charger.
  const getSource = (direction) =>
    direction === "INCOMING" ? "Charger" : "CMS";

  const getSourceChipStyles = (source) => {
    const isCharger = source === "Charger";
    const colorSource = isCharger ? theme.palette.success : theme.palette.info;
    return {
      bgcolor: alpha(colorSource.main, theme.palette.mode === "dark" ? 0.35 : 0.15),
      color: theme.palette.mode === "dark" ? colorSource.light : colorSource.dark,
      borderColor: alpha(colorSource.main, 0.4),
      borderWidth: 1,
      borderStyle: "solid",
      fontWeight: 600,
      height: 22,
    };
  };

  const getSourceIcon = (source) =>
    source === "Charger" ? "mdi:ev-station" : "mdi:send";

  // OCPP frame: [messageTypeId, uniqueId, ...]
  // 2 = Call (element[2] is the action name), 3 = CallResult, 4 = CallError.
  const getMessageTypeLabel = (message) => {
    try {
        const parsedMessage = JSON.parse(message);
        const messageTypeId = parsedMessage?.[0];
        if (messageTypeId === 2 && typeof parsedMessage?.[2] === "string") {
          return parsedMessage[2];
        }
        if (messageTypeId === 3) return "CallResult";
        if (messageTypeId === 4) return "CallError";
        return "UNKNOWN";
    } catch (error) {
      return "INVALID";
    }
  }

  const getErrorStatus = (message) => {
    try {
      const parsedMessage = JSON.parse(message);
      const errorCode = parsedMessage?.[3]?.errorCode;
      if(errorCode !== undefined && errorCode !== "NoError"){
        return false;
      }
      return true;
    } catch (error) {
      return "Invalid";
    }
  }

  const formatPayload = (message) => {
    try {
      return JSON.stringify(JSON.parse(message), null, 2);
    } catch (error) {
      return message;
    }
  }

  const totalEntries = data?.pages?.flat()?.length ?? 0;

  // Flatten all logs across pages so each gets a stable global index for expansion.
  const rows = [];
  if (Array.isArray(data?.pages)) {
    data.pages.forEach((page) => {
      page?.forEach((log) => rows.push(log));
    });
  }

  const gridTemplate = "180px 130px 130px 1fr";

  useLayoutEffect(() => {
    if (scrollRef.current && savedScrollTop.current > 0) {
      scrollRef.current.scrollTop = savedScrollTop.current;
    }
  }, [rows.length]);

  return(
    <>
      <Typography variant="h6" mb={2}>OCPP Communication Logs ({totalEntries} entries)</Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: gridTemplate,
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          bgcolor: headerBg,
          border: 1,
          borderColor: 'divider',
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
          fontWeight: 700,
          fontSize: '0.75rem',
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        <Box>Time</Box>
        <Box>Source</Box>
        <Box>Action</Box>
        <Box>Payload</Box>
      </Box>
      <Box
        id="logsScrollableDiv"
        ref={scrollRef}
        onScroll={(e) => {
          if (e.target.scrollTop > 0) savedScrollTop.current = e.target.scrollTop;
        }}
        sx={{
          height: '460px',
          overflow: 'auto',
          borderLeft: 1,
          borderRight: 1,
          borderBottom: 1,
          borderColor: 'divider',
          borderBottomLeftRadius: 4,
          borderBottomRightRadius: 4,
        }}
      >
        <InfiniteScroll
          dataLength={totalEntries}
          next={fetchNextPage}
          hasMore={hasNextPage}
          loader={
            <Typography sx={{ textAlign: "center", py: 1 }} variant="body2">
              Loading more logs...
            </Typography>
          }
          scrollableTarget="logsScrollableDiv"
        >
          {rows.map((log, index) => {
            const { messageTime, messageType, message } = log;
            const isSuccess = !!getErrorStatus(message);
            const isExpanded = expandedRow === index;
            const source = getSource(messageType);
            return (
              <Fragment key={index}>
                <Box
                  onClick={() => setExpandedRow(isExpanded ? null : index)}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: gridTemplate,
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    cursor: 'pointer',
                    borderBottom: 1,
                    borderColor: 'divider',
                    borderLeft: '3px solid',
                    borderLeftColor: isSuccess
                      ? theme.palette.success.main
                      : theme.palette.error.main,
                    bgcolor: isExpanded ? logBodyBg : 'transparent',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.action.hover, 0.6),
                    },
                  }}
                >
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Icon
                      icon={isExpanded ? "mdi:chevron-down" : "mdi:chevron-right"}
                      fontSize="small"
                      color={theme.palette.text.secondary}
                    />
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {formatLogTime(messageTime)}
                    </Typography>
                  </Box>
                  <Box>
                    <Chip
                      label={source}
                      icon={
                        <Icon
                          icon={getSourceIcon(source)}
                          fontSize="small"
                        />
                      }
                      sx={getSourceChipStyles(source)}
                      size="small"
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {getMessageTypeLabel(message)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontFamily: 'Consolas, "SFMono-Regular", "Courier New", monospace',
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                    }}
                  >
                    {message}
                  </Box>
                </Box>
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box
                    component="pre"
                    sx={{
                      whiteSpace: "pre-wrap",
                      bgcolor: logBodyBg,
                      wordWrap: "break-word",
                      m: 0,
                      px: 2,
                      py: 1.5,
                      borderBottom: 1,
                      borderColor: 'divider',
                      color: "text.primary",
                      fontSize: '0.75rem',
                      fontFamily: 'Consolas, "SFMono-Regular", "Courier New", monospace',
                    }}
                  >
                    {formatPayload(message)}
                  </Box>
                </Collapse>
              </Fragment>
            );
          })}
        </InfiniteScroll>
      </Box>
    </>
  )
}

export const OldLogsData = ({data,fetchNextPage,hasNextPage})=>{
  const { timezone } = useContext(Context);
  const formatLogTime = (messageTime) =>
    timezone.timezone?.value
      ? yearFirstDateTimeFormatInTimezone(messageTime, timezone.timezone.value)
      : yearFirstDateTimeFormat2(messageTime);
  return(
    <InfiniteScroll
          dataLength={data?.pages?.flat()?.length ?? 0}
          next={fetchNextPage}
          hasMore={hasNextPage}
          loader={<h4>Loading more logs...</h4>}
          scrollableTarget="scrollableDiv"
        >
          {data?.pages.map((page, index) => (
            <Fragment key={index}>
              {page?.map(({ messageTime, messageType, message }, index) => (
                <Box mb={2} key={index}>
                  <Typography
                    variant="caption"
                    gutterBottom
                    sx={{
                      color: messageType === "INCOMING" ? "blue" : "green",
                      marginRight: "10px",
                      fontSize: "16px",
                    }}
                  >
                    {messageType}:
                  </Typography>
                  <Typography
                    variant="caption"
                    gutterBottom
                    sx={{ marginRight: "10px", fontSize: "16px" }}
                  >
                    {formatLogTime(messageTime)} -
                  </Typography>

                  <Typography
                    variant="caption"
                    gutterBottom
                    sx={{ fontSize: "16px", wordWrap: "break-word" }}
                  >
                    {message}
                  </Typography>
                </Box>
              ))}
            </Fragment>
          ))}
        </InfiniteScroll>
  )
}